import {Injectable} from '@angular/core';
import {collection, collectionData, doc, DocumentData, Firestore, getDoc, writeBatch} from '@angular/fire/firestore';
import {encodeStrict, generateFirebaseCollectionReference} from '../../shared-utils/firestore/firestore.utils';
import {IFirebaseQuery} from '../../shared-models/firebase/firebase-queries';
import {IStore} from '../../shared-models/store/store';
import {Observable} from 'rxjs';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';
import {map} from 'rxjs/operators';
import {castObjectToType} from '../../shared-utils/object/object.utils';
import {createSharedPaths} from '../database-paths.shared';
import {IStockSupplierFieldsFromFirestore} from '../../../features/stock-manager/models/supplier-code-auto-fill.interface';
import {IPlannedStockTagWrite, planStockTagWriteBatches} from './collection-shared-stock-tags.utils';

@Injectable({
  providedIn: 'root',
})
export class CollectionSharedStockService {


  constructor(private firestore: Firestore) {
  }

  // ====================================================================================================
  // Stock
  // ====================================================================================================

  getStockItemsCast(
    store: IStore,
    firebaseQueries: IFirebaseQuery[],
    idField: string,
  ): Observable<StockItem[]> {
    const collectionRef = collection(this.firestore, createSharedPaths().setStoreId(store.storeId).stock());
    const q = generateFirebaseCollectionReference(firebaseQueries, collectionRef);
    return collectionData(q, {idField})
      .pipe(
        map((documents: DocumentData[]) => {
          return documents.map((doc: DocumentData) => castObjectToType<StockItem, DocumentData>(doc));
        }),
      );
  }


  async updateStockTags(tagedItems: Record<string, string[]>, store: IStore): Promise<void> {
    const paths = createSharedPaths().setStoreId(store.storeId);
    const plannedBatches = planStockTagWriteBatches(tagedItems);

    for (const plannedWrites of plannedBatches) {
      const batch = writeBatch(this.firestore);
      plannedWrites.forEach((write: IPlannedStockTagWrite) => {
        const encodedStockId = encodeStrict(write.stockId);
        switch (write.target) {
          case 'stock':
            batch.update(doc(this.firestore, paths.stock(), encodedStockId), {['_tags']: write.tags});
            break;
          case 'cache':
            batch.set(
              doc(this.firestore, paths.lastUpdatedStockTags(), encodedStockId),
              {['_tags']: write.tags},
              {merge: true},
            );
            break;
          default: {
            const unhandled: never = write.target;
            throw new Error(`Unhandled stock tag write target: ${unhandled}`);
          }
        }
      });
      await batch.commit();
    }
  }


  /**
   * Reads multipleSuppliers and supplierDetails from a stock document in Firestore.
   */
  async getStockSupplierFieldsByCode(storeId: string, stockCode: string): Promise<IStockSupplierFieldsFromFirestore> {
    const stockPath = createSharedPaths().setStoreId(storeId).stock();
    const snap = await getDoc(doc(this.firestore, `${stockPath}/${encodeStrict(stockCode)}`));
    if (!snap.exists()) {
      return {multipleSuppliers: [], supplierDetails: []};
    }
    const raw = snap.data();
    const multipleSuppliersRaw = raw['multipleSuppliers'] ?? [];
    const multipleSuppliers = Array.isArray(multipleSuppliersRaw)
      ? multipleSuppliersRaw.map((code: unknown) => String(code))
      : [];
    return {
      multipleSuppliers,
      supplierDetails: raw['supplierDetails'] ?? [],
    };
  }

  async updateMultipleSuppliers(multipleSuppliers: Record<string, string[]>, store: IStore): Promise<void> {
    const BATCH_SIZE = 500;
    const stockIds = Object.keys(multipleSuppliers);

    const batches = [];
    for (let i = 0; i < stockIds.length; i += BATCH_SIZE) {
      const batch = writeBatch(this.firestore);
      const chunk = stockIds.slice(i, i + BATCH_SIZE);

      chunk.forEach((stockId: string) => {
        const stockRef = doc(this.firestore, createSharedPaths().setStoreId(store.storeId).stock(), stockId);
        const suppliers = multipleSuppliers[stockId];

        batch.update(stockRef, {['multipleSuppliers']: suppliers});
      });
      batches.push(batch);
    }

    for (const [index, batch] of batches.entries()) {
      try {
        await batch.commit();
      } catch (error) {
        console.error(`Error committing batch ${index + 1}:`, error);
      }
    }
  }

}
